import { apiRequest, createUrlWithCenterId } from '../utils/apiHelpers';
import { sortByNombre } from '../utils/sortByNombre';

export interface Sucursal {
  id: number;
  clienteId: number;
  cliente?: {
    id: number;
    nombre: string;
  };
  nombre: string;
  direccion?: string;
  telefono?: string;
  localidadId: number;
  localidad?: {
    id: number;
    nombre: string;
    provincia?: {
      id: number;
      nombre: string;
      codigo: string;
      paisId: number;
      pais?: {
        id: number;
        nombre: string;
      };
    };
  };
  mapaDePuestosArchivoId?: number;
  activo: boolean;
  mail?: string;
  usuarioIds?: number[];
}

export interface Localidad {
  id: number;
  nombre: string;
  provinciaId: number;
  provincia?: {
    id: number;
    nombre: string;
    codigo: string;
    paisId: number;
  };
}

export interface Provincia {
  id: number;
  nombre: string;
  codigo: string;
  paisId: number;
}

export interface Pais {
  id: number;
  nombre: string;
  codigo: string;
}

function normalizeSucursal(raw: any): Sucursal {
  const usuarioIdsRaw = raw.usuarioIds ?? raw.UsuarioIds;
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    clienteId: raw.clienteId ?? raw.ClienteId,
    nombre: raw.nombre ?? raw.Nombre ?? '',
    activo: raw.activo ?? raw.Activo ?? true,
    usuarioIds: Array.isArray(usuarioIdsRaw)
      ? usuarioIdsRaw.map((id: any) => Number(id)).filter((id: number) => !Number.isNaN(id))
      : [],
  };
}

export const sucursalService = {
  // Obtener todas las sucursales
  getAll: async (): Promise<Sucursal[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales');
    const rows = await apiRequest(endpoint);
    return sortByNombre((rows || []).map(normalizeSucursal));
  },

  getAllForAdmin: async (): Promise<Sucursal[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/admin');
    const rows = await apiRequest(endpoint);
    return sortByNombre((rows || []).map(normalizeSucursal));
  },

  // Obtener sucursal por ID
  getById: async (id: number): Promise<Sucursal> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/${id}`);
    return normalizeSucursal(await apiRequest(endpoint));
  },

  // Obtener sucursales por cliente
  getByClienteId: async (clienteId: number): Promise<Sucursal[]> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/cliente/${clienteId}`);
    const rows = await apiRequest(endpoint);
    return sortByNombre((rows || []).map(normalizeSucursal));
  },

  getComboList: async (): Promise<{ id: number; nombre: string; clienteId: number }[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/combolist');
    const rows = await apiRequest(endpoint);
    return sortByNombre(
      (rows || []).map((s: any) => ({
        id: s.id ?? s.Id,
        nombre: s.nombre ?? s.Nombre ?? '',
        clienteId: s.clienteId ?? s.ClienteId,
      })),
    );
  },

  // Obtener sucursales activas
  getActivas: async (): Promise<Sucursal[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/activas');
    const rows = await apiRequest(endpoint);
    return sortByNombre((rows || []).map(normalizeSucursal));
  },

  // Crear nueva sucursal
  create: async (sucursal: Omit<Sucursal, 'id'>): Promise<Sucursal> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales');
    return await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(sucursal),
    });
  },

  // Actualizar sucursal
  update: async (id: number, sucursal: Partial<Sucursal>): Promise<void> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/${id}`);
    await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(sucursal),
    });
  },

  // Eliminar sucursal (borrado lógico)
  delete: async (id: number): Promise<{ message: string }> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/${id}`);
    return await apiRequest(endpoint, {
      method: 'DELETE',
    });
  },

  // Reactivar sucursal
  reactivar: async (id: number): Promise<{ message: string }> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/${id}/reactivar`);
    return await apiRequest(endpoint, {
      method: 'PUT',
    });
  },

  // Obtener localidades por provincia
  getLocalidadesByProvincia: async (provinciaId: number): Promise<Localidad[]> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/localidades/${provinciaId}`);
    return await apiRequest(endpoint);
  },

  // Obtener provincias por país
  getProvinciasByPais: async (paisId: number): Promise<Provincia[]> => {
    const endpoint = createUrlWithCenterId(`/api/Sucursales/provincias/${paisId}`);
    return await apiRequest(endpoint);
  },

  // Obtener países
  getPaises: async (): Promise<Pais[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/paises');
    return await apiRequest(endpoint);
  },

  // Obtener provincias
  getProvincias: async (): Promise<Provincia[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/provincias');
    return await apiRequest(endpoint);
  },

  // Obtener localidades
  getLocalidades: async (): Promise<Localidad[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/localidades');
    return await apiRequest(endpoint);
  },

  // Obtener clientes
  getClientes: async (): Promise<any[]> => {
    const endpoint = createUrlWithCenterId('/api/Sucursales/clientes');
    return await apiRequest(endpoint);
  },
};
